/**
 * Fuze Floor-Plan Engine — public API.
 *
 * A POS-agnostic Skia floor-plan engine: it renders, transforms, and edits
 * generic `FloorObject`s from a versioned JSON document. It knows NOTHING
 * about stores/units/reservations — domain meaning is injected through
 * `FloorObject.metadata` by the consuming layer
 * (`containers/Store/FloorPlan/`).
 *
 * Consumers import from THIS barrel (or a deeper path for tree-shaking);
 * nothing outside `src/floorplan/` should reach into internal structure.
 *
 * @module floorplan
 * @category FloorPlan
 */

// Models + constants
export * from './constants';
export * from './models';

// History (command-based undo/redo) + editing-command factories
export type {
  CommandTarget,
  FloorCommand,
  PatchEntry,
  ZOrderDirection,
} from './history';
export {
  CommandStack,
  CompositeCommand,
  InsertObjectsCommand,
  makeDuplicateCommand,
  makeGroupCommand,
  makeInsertCommand,
  makeMoveCommand,
  makePasteCommand,
  makeRemoveCommand,
  makeTransformCommand,
  makeUngroupCommand,
  makeZOrderCommand,
  PatchObjectsCommand,
  RemoveObjectsCommand,
} from './history';

// Selection transform handles
export type { HandleId, ResizeHandleId } from './selection/handles';
export {
  applyFrameScale,
  HANDLE_DIRECTIONS,
  handleWorldPositions,
  hitTestHandle,
  magneticAngle,
  MIN_RESIZE_SIZE,
  resizeAnchor,
  rotationPatches,
  scaleFromDelta,
} from './selection/handles';

// Snapping + alignment
export type {
  AlignEdge,
  AlignPatch,
  AxisSnap,
  ResolveSnapOptions,
  SnapGuide,
  SnapResult,
} from './snapping';
export {
  alignObjects,
  distributeObjects,
  NO_SNAP,
  resolveSnap,
  snapBoundsToGrid,
  snapBoundsToObjects,
  snapValueToGrid,
} from './snapping';

// Serialization (save/load/import/export + migrations)
export type {
  FloorPlanMigration,
  ParseFloorPlanOptions,
  RawFloorPlanDocument,
} from './serialization';
export {
  createEmptyFloorPlan,
  FLOOR_PLAN_MIGRATIONS,
  FLOOR_PLAN_SCHEMA_VERSION,
  FloorPlanMigrationError,
  floorPlanSchema,
  migrateFloorPlan,
  parseFloorPlan,
  serializeFloorPlan,
} from './serialization';
export type {
  FloorPlanKeyValueStorage,
  FloorPlanRepositoryOptions,
  FloorPlanSummary,
} from './serialization/repository';
export { FloorPlanRepository } from './serialization/repository';

// Engine store (editor state authority)
export type {
  FloorEngineActions,
  FloorEngineState,
  FloorEngineStore,
} from './stores/useFloorEngineStore';
export {
  initialState as floorEngineInitialState,
  selectFloorPlanDocument,
  useFloorEngineStore,
} from './stores/useFloorEngineStore';

// Rendering core (Skia)
export type { CameraGestureOptions } from './gestures/useCameraGestures';
export { useCameraGestures } from './gestures/useCameraGestures';
export type { UseFloorEditorOptions } from './hooks/useFloorEditor';
export {
  FLOOR_DRAG_MODE,
  default as useFloorEditor,
} from './hooks/useFloorEditor';
export type { FloorHistory } from './hooks/useFloorHistory';
export { default as useFloorHistory } from './hooks/useFloorHistory';
export { default as useFloorKeyboard } from './hooks/useFloorKeyboard';
export type { UseFloorPictureOptions } from './hooks/useFloorPicture';
export { default as useFloorPicture } from './hooks/useFloorPicture';
export type { FloorThemeResult } from './hooks/useFloorTheme';
export { default as useFloorTheme } from './hooks/useFloorTheme';
export { default as useFloorWheel } from './hooks/useFloorWheel';
export { default as useGridPicture } from './hooks/useGridPicture';
export { default as FloorCanvas } from './renderer/FloorCanvas';
export type { GuideChannels } from './renderer/GuidePlane';
export { default as GuidePlane } from './renderer/GuidePlane';
export type { MarqueeChannels } from './renderer/MarqueePlane';
export { default as MarqueePlane } from './renderer/MarqueePlane';
export {
  recordFloorPicture,
  recordGridPicture,
} from './renderer/recordPicture';
export {
  registerBuiltInRenderers,
  registerRenderer,
  resolveRegistryHitShape,
  resolveRenderer,
  unregisterRenderer,
} from './renderer/RendererRegistry';
export { default as SelectionPlane } from './renderer/SelectionPlane';
export type {
  FloorTheme,
  ObjectRenderer,
  RenderContext,
  RendererEntry,
} from './renderer/types';
export type { FloorCamera } from './viewport/useFloorCamera';
export {
  cameraSnapshot,
  useFloorCamera,
  zoomCameraToBounds,
} from './viewport/useFloorCamera';

// Tools (DRAW-mode plugins)
export type { DraftChannels } from './renderer/DraftPlane';
export { default as DraftPlane } from './renderer/DraftPlane';
export type { CreateObjectOptions } from './tools/ToolRegistry';
export {
  createObjectFromTool,
  listTools,
  nextZIndexForLayer,
  registerBuiltInTools,
  registerTool,
  resolveTargetLayerId,
  resolveTool,
  unregisterTool,
} from './tools/ToolRegistry';
export type { ShapeToolDefinition } from './tools/types';

// Geometry / camera / hit-test utilities
export {
  boundsContainPoint,
  boundsIntersect,
  boundsUnion,
  effectiveSize,
  normalizeBounds,
  objectAabb,
  objectCenter,
  objectCorners,
  objectsAabb,
  rotatePoint,
} from './utils/bounds';
export {
  cameraToFitBounds,
  clamp,
  clampZoom,
  screenToWorld,
  visibleWorldBounds,
  worldToScreen,
  zoomAboutPoint,
} from './utils/camera';
export { cullObjects } from './utils/culling';
export type {
  HitShape,
  HitTestOptions,
  ResolveHitShape,
} from './utils/hitTest';
export {
  defaultResolveHitShape,
  hitTestObject,
  hitTestTopmost,
  pointInEllipse,
  pointInRotatedRect,
} from './utils/hitTest';
export { createFloorId } from './utils/id';
